import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withCtx as _withCtx, createVNode as _createVNode, resolveComponent as _resolveComponent, createTextVNode as _createTextVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "px-2" }
const _hoisted_2 = { class: "text-3xl font-extrabold leading-8 mb-1 text-top" }
const _hoisted_3 = { class: "text-lg font-semibold opacity-80 leading-6 text-top-faded" }
const _hoisted_4 = { class: "space-y-4 py-4" }
const _hoisted_5 = { class: "space-x-2 flex-nowrap flex justify-start" }

import { computed, inject } from "vue";
import NavigationHeader from "@/features/shared/NavigationHeader.vue";
import preChatSurveyPersistence from "@/utils/preChatSurveyPersistence";
import ActionButton from "@/features/shared/ActionButton.vue";
import { FormKitGroupValue, FormKitSchemaNode } from "@formkit/core";
import mitt from "@/utils/mitt";
import {
  GChatPreChatSurveyFieldTargetEntityType,
  GConnectedServiceChatEntityConversationStartModeItem,
  GWidgetConfigurationPreChatSurveyField,
} from "@/generated/graphql";
import { useContactPersistence } from "@/utils/contactPersistence";
import { createConversation } from "@/utils/createConversation";
import { tenantInjectionKey, connectedServiceInjectionKey } from "@/keys";
import { settingsInjectionKey, demoModeInjectionKey } from "../../keys";


export default /*#__PURE__*/_defineComponent({
  __name: 'PreChatSurvey',
  emits: ["close", "back"],
  setup(__props, { emit: __emit }) {

const fieldTypeToFormkitTypeMap: Record<GWidgetConfigurationPreChatSurveyField["type"], string> = {
  NAME: "text",
  EMAIL: "email",
  PHONE_NUMBER: "text",
  NUMBER: "number",
  UNKNOWN: "",
  TEXT: "text",
  MULTILINE_TEXT: "textarea",
  DATE: "datepicker",
  DATE_TIME: "datepicker",
  RADIO: "radio",
  CHECKBOX: "checkbox",
  DROPDOWN: "select",
};

const fieldTypeValidationMap: Record<GWidgetConfigurationPreChatSurveyField["type"], string> = {
  NAME: "alpha_spaces:default",
  EMAIL: "email",
  PHONE_NUMBER: "phone",
  NUMBER: "",
  UNKNOWN: "",
  TEXT: "",
  MULTILINE_TEXT: "",
  DATE: "",
  DATE_TIME: "",
  RADIO: "",
  CHECKBOX: "require_one",
  DROPDOWN: "",
};

const emits = __emit;

const settings = inject(settingsInjectionKey);
const demoMode = inject(demoModeInjectionKey);
const tenant = inject(tenantInjectionKey);
const connectedService = inject(connectedServiceInjectionKey);

const contactPersistence = useContactPersistence(tenant);

const compileValidationProp = (isRequired: boolean, validator?: string) => {
  return isRequired ? (validator ? `required|${validator}` : `required`) : validator;
};

const persistedState = preChatSurveyPersistence.getState();

const schema = computed(() => {
  const res: Array<FormKitSchemaNode> = [];

  if (!settings?.value.preChatSurveyFields) {
    return res;
  }

  settings.value.preChatSurveyFields.forEach(field => {
    const value = persistedState
      ?.filter(i => i.targetEntity === GChatPreChatSurveyFieldTargetEntityType.Contact)
      ?.find(i => i.id === field.id)?.value;

    const obj: FormKitSchemaNode = {
      name: field.id,
      $formkit: fieldTypeToFormkitTypeMap[field.type],
      validation: compileValidationProp(field.required, fieldTypeValidationMap[field.type]),
      label: field.label,
      help: field.hint,
      options: field.options,
      value: value,
    };

    if (field.placeholder) {
      obj.attrs = {
        placeholder: field.placeholder,
      };
    }

    res.push(obj);
  });

  return res;
});

function onCloseClick() {
  emits("close");
}

function onBackClick() {
  emits("back");
}

async function submit(form: FormKitGroupValue) {
  if (demoMode || !tenant) {
    console.log("Aborting submit -- demo mode");
    return;
  }

  if (settings?.value.preChatSurveyFields) {
    preChatSurveyPersistence.saveState(
      settings.value
        .preChatSurveyFields!.filter(i => form[i.id])
        .map(field => {
          return {
            id: field.id,
            label: field.label,
            type: field.type,
            targetEntity: field.targetEntity,
            value: form[field.id],
          };
        })
    );
  }

  const state = preChatSurveyPersistence.getState();
  const contact = contactPersistence.getState();

  const text = state
    ?.filter(i => i.value)
    .map(field => `<b>${field.label}:</b> ${field.value ?? ""}`)
    .join("<br />");

  if (
    text &&
    settings!.value.behaviour.conversationStartMode ===
      GConnectedServiceChatEntityConversationStartModeItem.StartOnSurveySubmission
  ) {
    try {
      const res = await createConversation(text ?? "<no message>", contact, tenant, connectedService!);

      if (res.error) {
        throw new Error(`Creating conversation failed: ${res.error}`);
      }
    } catch (err) {
      mitt.emit("error", "Samtalen kunne ikke startes");
      console.error("Error starting conversation", err);
      return;
    }
  }

  mitt.emit("go-to-conversation");
}

return (_ctx: any,_cache: any) => {
  const _component_FormKitSchema = _resolveComponent("FormKitSchema")!
  const _component_FormKit = _resolveComponent("FormKit")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(NavigationHeader, {
      "include-back": "",
      onClose: onCloseClick,
      onBack: onBackClick
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, _toDisplayString(_unref(settings)?.text?.preChatSurveyHeader), 1),
          _createElementVNode("div", _hoisted_3, _toDisplayString(_unref(settings)?.text?.preChatSurveySubHeader), 1)
        ])
      ]),
      _: 1
    }),
    _createElementVNode("div", _hoisted_4, [
      _createVNode(_component_FormKit, {
        type: "form",
        "form-class": "px-4 space-y-3 sm:p-6",
        onSubmit: submit,
        actions: false
      }, {
        default: _withCtx((context) => [
          _createVNode(_component_FormKitSchema, { schema: schema.value }, null, 8, ["schema"]),
          _createVNode(_component_FormKit, { type: "submit" }, {
            input: _withCtx(() => [
              _createElementVNode("div", _hoisted_5, [
                _createVNode(ActionButton, {
                  type: "submit",
                  loading: context.state.loading ?? false,
                  onClick: context.node.submit
                }, {
                  default: _withCtx(() => [
                    _createTextVNode("Start samtale")
                  ]),
                  _: 2
                }, 1032, ["loading", "onClick"])
              ])
            ]),
            _: 2
          }, 1024)
        ]),
        _: 1
      })
    ])
  ], 64))
}
}

})